/* @jsxImportSource @emotion/react */
import React from 'react'

import { INLINE_BLOCK_BACKGROUND } from './utils'

export type InlineCtaProps = {
  title: string
  description?: string | null
  buttonLabel: string
  buttonUrl: string
  image?: {
    url?: string | null
    alt?: string | null
    fit?: string | null
  } | null
}

function inlineCtaImageUrl(url: string) {
  const optimized = new URL(url)
  optimized.searchParams.set('auto', 'format')
  optimized.searchParams.set('q', '75')
  optimized.searchParams.set('max-w', '1200')
  return optimized.toString()
}

const SMALL_BREAKPOINT = '@media screen and (max-width: 40rem)'
const TITLE_CLASS = 'font-family-title font-weight-medium shade-00'
const DESCRIPTION_CLASS = 'shade-10'

const cardCss = {
  borderRadius: 24,
  background: INLINE_BLOCK_BACKGROUND,
} as const

const titleCss = { fontSize: 26, lineHeight: 1.3 } as const

const descriptionCss = { fontSize: 15, lineHeight: 1.5 } as const

// &&& outranks the .simple-rich-text a rule
const buttonCss = {
  '&&&': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 20px',
    borderRadius: 999,
    backgroundColor: 'var(--shade-00)',
    color: 'var(--shade-100)',
    fontFamily: 'var(--font-family-system)',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    transition: 'opacity .2s ease',
    ':hover': {
      opacity: 0.85,
      color: 'var(--shade-100)',
    },
  },
} as const

const imageCardCss = {
  gridTemplateColumns: '1fr 1fr',
  [SMALL_BREAKPOINT]: { gridTemplateColumns: '1fr' },
} as const

const imageCss = {
  '&&&': {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
  },
} as const

const imageCardContentCss = {
  alignSelf: 'stretch',
  padding: '32px 32px 32px 0px',
  [SMALL_BREAKPOINT]: { padding: '0 32px 32px' },
} as const

export function InlineCta({
  title,
  description,
  buttonLabel,
  buttonUrl,
  image,
}: InlineCtaProps) {
  const imageUrl = image?.url

  const button = (
    <a href={buttonUrl} css={buttonCss}>
      {buttonLabel}
    </a>
  )

  if (!imageUrl) {
    return (
      <aside
        className="margin-vertical-32 padding-horizontal-40 flexbox flexbox-column flexbox-align-center flexbox-gap-16 text-align-center"
        css={[cardCss, { paddingTop: 48, paddingBottom: 48 }]}
      >
        <div className={TITLE_CLASS} css={[titleCss, { maxWidth: 480 }]}>
          {title}
        </div>
        {!!description && (
          <div
            className={DESCRIPTION_CLASS}
            css={[descriptionCss, { maxWidth: 560 }]}
          >
            {description}
          </div>
        )}
        {button}
      </aside>
    )
  }

  return (
    <aside
      className="grid margin-vertical-32 grid-gap-32 grid-align-center overflow-hidden"
      css={[cardCss, imageCardCss]}
    >
      <div
        className="position-relative"
        css={{ minHeight: 300, alignSelf: 'stretch' }}
      >
        {/* Plain img: ImgSanity's lazy loading breaks absolutely-positioned images in Safari */}
        <img
          src={inlineCtaImageUrl(imageUrl)}
          alt={image?.alt || ''}
          css={[
            imageCss,
            {
              '&&&': {
                objectFit: image?.fit === 'contain' ? 'contain' : 'cover',
              },
            },
          ]}
        />
      </div>
      <div
        className="flexbox flexbox-column flexbox-align-start flexbox-gap-16 text-align-left"
        css={imageCardContentCss}
      >
        <div className={TITLE_CLASS} css={titleCss}>
          {title}
        </div>
        {!!description && (
          <div className={DESCRIPTION_CLASS} css={descriptionCss}>
            {description}
          </div>
        )}
        <div className="margin-top-auto">{button}</div>
      </div>
    </aside>
  )
}
